import { Global, css, useTheme } from '@emotion/react';
import { fontFamilyFromPath, sanitizeFontPath } from 'utils/font';

const FORMAT_MAP: Record<string, string> = {
  otf: 'opentype',
  ttf: 'truetype',
  woff: 'woff',
  woff2: 'woff2',
  eot: 'embedded-opentype',
};

function getFormatFromPath(path: string): string | null {
  const ext = path.split('.').pop()?.toLowerCase() ?? '';
  return FORMAT_MAP[ext] ?? null;
}

interface FontEntry {
  path: string;
  weight: string;
}

function buildFontFaceStyles(fontFamily: string, entries: FontEntry[]) {
  const declarations = entries
    .map(({ path, weight }) => {
      const format = getFormatFromPath(path);
      const src = format
        ? `url('${path}') format('${format}')`
        : `url('${path}')`;
      return `
    @font-face {
      font-family: '${fontFamily}';
      src: ${src};
      font-weight: ${weight};
      font-style: normal;
      font-display: swap;
    }`;
    })
    .join('\n');

  return css`
    ${declarations}
  `;
}

export const WhiteLabelFontLoader: React.FC = () => {
  const theme = useTheme();

  const fonts = theme.whiteLabelTheme?.fonts;
  if (!fonts) {
    return null;
  }

  const fontFamily = fontFamilyFromPath(fonts.normal ?? '', 'WhiteLabelFont');

  const candidates: { raw?: string; weight: string }[] = [
    { raw: fonts.light, weight: '300' },
    { raw: fonts.normal, weight: '400' },
    { raw: fonts.bold, weight: '700' },
  ];
  const entries: FontEntry[] = candidates.reduce<FontEntry[]>(
    (acc, { raw, weight }) => {
      const path = raw ? sanitizeFontPath(raw) : null;
      if (path) acc.push({ path, weight });
      return acc;
    },
    []
  );

  if (!entries.length) {
    return null;
  }

  return <Global styles={buildFontFaceStyles(fontFamily, entries)} />;
};
